import { Center, VStack, Heading, Spinner, Text, Thead, Tr, Th, Tbody, Td, Box, Table as ChakraTable, Button, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useDisclosure, useToast, FormControl, FormLabel, HStack, IconButton } from "@chakra-ui/react"
import { IUseSegments } from "@olagg/api-hooks/interfaces/IUseSegments"
import useAdminSegments from "./useAdminSegments"
import Pagination from "@olagg/ui-kit/Pagination"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../consts/routes";
import { FaPlus, FaUserPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { OlaggModule, PermissionEnum } from "@olagg/db-types"
import useRoles from "../../features/Roles/hooks/useRoles"
import { AsyncSelect } from 'chakra-react-select'
import { fetcher, AdminSegmentEndpoints, AdminUserEndpoints } from '@olagg/api-hooks'

const Segments = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState<IUseSegments>({
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
    name: searchParams.get('name') || ''
  })
  const [selectedSegment, setSelectedSegment] = useState<any>(null)
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [isAssigning, setIsAssigning] = useState(false)

  const { limit, page } = filter;

  const { segments, count, isLoading, mutate } = useAdminSegments({ page, limit });
  const { missingPermission } = useRoles();

  const handleSearchUsers = async (inputValue: string) => {
    return await fetcher(
      AdminUserEndpoints.all({ page: 1, limit: 10, query: inputValue })
    ).then((response) => {
      const { users } = response;
      return users.map((user: any) => ({
        value: user.id,
        label: `${user.name} (${user.email})`
      }));
    });
  };

  const handleOpenAssignModal = (segment: any) => {
    setSelectedSegment(segment)
    setSelectedUsers([])
    onOpen()
  }

  const handleAssignUsers = async () => {
    if (!selectedSegment || selectedUsers.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar al menos un usuario',
        status: 'error',
        duration: 3000,
      })
      return
    }

    setIsAssigning(true)
    try {
      const userIds = selectedUsers.map(u => u.value)
      await fetcher(AdminSegmentEndpoints.assignUsers(selectedSegment.id, userIds))
      toast({
        title: 'Éxito',
        description: `${userIds.length} usuario(s) asignado(s) al segmento correctamente`,
        status: 'success',
        duration: 3000,
      })
      onClose()
      mutate()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Error al asignar usuarios',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsAssigning(false)
    }
  }

  const handleDelete = async (segment: any) => {
    if (!confirm(`¿Estás seguro de que deseas eliminar el segmento "${segment.name}"?`)) {
      return
    }

    try {
      await fetcher(AdminSegmentEndpoints.delete(segment.id))
      toast({
        title: 'Éxito',
        description: 'Segmento eliminado correctamente',
        status: 'success',
        duration: 3000,
      })
      mutate()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Error al eliminar el segmento',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    setFilter({ ...filter, [name]: value })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  return (
    <Center w={'full'} p={4} >
      <VStack color={'white'} w={'full'}>
        <Heading my={2}>
          Segmentos
        </Heading>

        <Flex justifyContent={'flex-end'} w={'100%'} alignItems={'center'}>
          <Button
            display='flex'
            alignSelf='self-end'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.SEGMENTS.create)}
            mr={2}
            isDisabled={missingPermission(OlaggModule.Notification, PermissionEnum.CREATE)}
          >
            Crear segmento
          </Button>
        </Flex>
      
      {isLoading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          segments?.length > 0 ? (
            <ChakraTable
              variant="striped"
              colorScheme="olaggPurple"
              fontSize="18px"
              fontWeight="semibold"
            >
              <Thead>
                <Tr>
                  <Th color="white">ID</Th>
                  <Th color="white">Nombre</Th>
                  <Th color="white">Descripción</Th>
                  <Th color="white">Creación</Th>
                  <Th color="white">Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {segments.map(segment => (
                  <Tr key={segment.id}>
                    <Td>{segment.id}</Td>
                    <Td>{segment.name}</Td>
                    <Td>{segment.description}</Td>
                    <Td>{segment.createdAt?.toLocaleString()}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Editar segmento"
                          icon={<FaEdit />}
                          size="sm"
                          colorScheme="blue"
                          onClick={() => navigate(ROUTES.SEGMENTS.edit(segment.id))}
                          isDisabled={missingPermission(OlaggModule.Notification, PermissionEnum.UPDATE)}
                        />
                        <IconButton
                          aria-label="Eliminar segmento"
                          icon={<FaTrash />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDelete(segment)}
                          isDisabled={missingPermission(OlaggModule.Notification, PermissionEnum.DELETE)}
                        />
                        <Button
                          size="sm"
                          leftIcon={<FaUserPlus />}
                          onClick={() => handleOpenAssignModal(segment)}
                          colorScheme="purple"
                          isDisabled={missingPermission(OlaggModule.Notification, PermissionEnum.UPDATE)}
                        >
                          Asignar usuarios
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </ChakraTable>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>Todavía no hay ningún segmento</Text>
            </Box>
          )
        )}
        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            } as EventTarget & HTMLInputElement)
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            } as EventTarget & HTMLInputElement)
          }
          hideLimitInput
        />

        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Asignar usuarios a {selectedSegment?.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel>Seleccionar usuarios</FormLabel>
                <AsyncSelect
                  isMulti
                  value={selectedUsers}
                  onChange={(value: any) => setSelectedUsers(value)}
                  loadOptions={handleSearchUsers}
                  placeholder="Buscar usuarios por nombre, email o ID..."
                  isClearable
                  chakraStyles={{
                    container: (provided) => ({ ...provided, bg: 'gray.700' }),
                    menuList: (provided) => ({ ...provided, bg: 'gray.700' }),
                    option: (provided, { isFocused }) => ({
                      ...provided,
                      bg: isFocused ? 'gray.600' : 'gray.700',
                    }),
                  }}
                />
                <Text fontSize="sm" color="gray.400" mt={2}>
                  Los dispositivos de estos usuarios serán añadidos al segmento
                </Text>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleAssignUsers}
                isLoading={isAssigning}
                loadingText="Asignando..."
              >
                Asignar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Center>
  )
}

export default Segments
